/** Resuelve el tipo de evidencia requerido por la pregunta. */
export function resolveEvidenceType(question: any, answerValue: any): string | null {
  if (missingEvidenceConfig(question)) return null;
  if (isFileAnswer(question)) return null;
  return typedEvidence(question, answerValue);
}

/** Indica si la pregunta no exige evidencia. */
function missingEvidenceConfig(question: any): boolean {
  return !question || !question.evidenceRequired;
}

/** Indica si la respuesta es un archivo directo. */
function isFileAnswer(question: any): boolean {
  return question.questionType?.dataType === 'file';
}

/** Resuelve la evidencia booleana o la configurada. */
function typedEvidence(question: any, answerValue: any): string | null {
  if (hasBooleanEvidence(question)) return booleanEvidence(question, answerValue);
  return fallbackEvidence(question);
}

/** Indica si la pregunta define evidencia por valor booleano. */
function hasBooleanEvidence(question: any): boolean {
  return Boolean(question.evidenceTrue || question.evidenceFalse);
}

/** Resuelve la evidencia según el valor sí o no. */
function booleanEvidence(question: any, answerValue: any): string | null {
  if (answerValue === 'true') return question.evidenceTrue;
  if (answerValue === 'false') return question.evidenceFalse;
  return null;
}

/** Obtiene el tipo configurado o archivo por defecto. */
function fallbackEvidence(question: any): string | null {
  if (question.evidenceType) return question.evidenceType;
  return 'file';
}

/** Traduce el tipo de evidencia a etiqueta visible. */
export function evidenceLabelOf(kind: string | null): string {
  return labeledKind(kind) || '—';
}

/** Obtiene la etiqueta de archivo o la de texto. */
function labeledKind(kind: string | null): string | null {
  if (kind === 'file') return 'Archivo';
  return textLabel(kind);
}

/** Obtiene la etiqueta de los tipos de texto. */
function textLabel(kind: string | null): string | null {
  if (kind === 'text') return 'Texto';
  if (kind === 'file_or_text') return 'Archivo o Texto';
  return null;
}

/** Obtiene el nombre original del archivo o genérico. */
export function fileNameOf(file: any): string {
  return file?.originalName || 'archivo';
}

/** Obtiene el id del archivo de evidencia. */
export function fileIdOf(target: any): any {
  return target.fileId || target.file?.id || null;
}

/** Indica si el objetivo tiene archivo o comentarios. */
export function hasEvidenceContent(target: any): boolean {
  if (target?.fileId) return true;
  return hasCommentText(target?.comments);
}

/** Indica si los comentarios tienen texto visible. */
function hasCommentText(comments: any): boolean {
  if (!comments) return false;
  return String(comments).trim() !== '';
}

/** Obtiene el primer archivo de un evento de carga. */
export function firstFileOf(event: Event): File | null {
  const input = event.target as HTMLInputElement;
  return input.files?.[0] || null;
}

/** Arma el formulario para subir un archivo. */
export function uploadFormData(file: File): FormData {
  const data = new FormData();
  data.append('file', file);
  return data;
}

/** Normaliza el valor de respuesta vacío a nulo. */
export function normalizeAnswerValue(value: any): any {
  if (value != null && String(value).trim() !== '') return value;
  return null;
}

/** Arma el payload de respuesta con valor normalizado. */
export function answerPayloadOf(target: any): any {
  return answerPayload(target.id, normalizeAnswerValue(target.answer), target);
}

/** Arma el payload de evidencia conservando el valor. */
export function targetPayloadOf(target: any): any {
  return answerPayload(target.id, target.answer ?? null, target);
}

/** Arma el payload base de guardado de respuesta. */
function answerPayload(id: string, answer: any, target: any): any {
  return { id, answer, comments: target.comments || '', fileId: target.fileId || null };
}

/** Arma el payload de revisión conservando el comentario del auditor. */
export function reviewPayloadOf(target: any): any {
  return reviewPayload(target.id, target.answer ?? null, target);
}

/** Arma el payload de guardado con comentario de revisión. */
function reviewPayload(id: string, answer: any, target: any): any {
  return { id, answer, comments: target.comments || '', fileId: target.fileId || null, reviewComment: target.reviewComment ?? null };
}

/** Texto de error de subida de evidencia. */
export function uploadErrorText(err: any): string {
  return err?.error?.error || 'No se pudo subir el archivo';
}
